import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

interface Project {
  id: string;
  title: string;
  institution: string;
  description: string;
  budget: number;
  deadline: string;
  status: string;
  services: any[];
  client_name: string;
  created_at: string;
  has_requested?: boolean;
  request_status?: string;
}

const BrowseProjects: React.FC = () => {
  const navigate = useNavigate();
  const [projects, setProjects] = useState<Project[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filters, setFilters] = useState({
    institution: '',
    min_budget: '',
    max_budget: '',
    service_type: '',
    search: '',
    status: 'pending',
    page: 1,
    limit: 10
  });
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalProjects: 0,
    hasNext: false,
    hasPrev: false
  });

  const [user, setUser] = useState<any>(null);

  useEffect(() => {
    const loadUserData = () => {
      try {
        const token = localStorage.getItem('token');
        const userData = localStorage.getItem('user');
        
        if (!token || !userData) {
          navigate('/signin');
          return;
        }

        const parsedUser = JSON.parse(userData);
        setUser(parsedUser);
        
        if (parsedUser.role !== 'freelancer') {
          navigate('/dashboard');
        }
      } catch (error) {
        console.error('Error loading user data:', error);
        navigate('/signin');
      }
    };

    loadUserData();
  }, [navigate]);

  const loadProjects = useCallback(async () => {
    try {
      setIsLoading(true);
      const token = localStorage.getItem('token');
      
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value.toString());
      });

      const response = await axios.get(
        `http://localhost:5000/api/projects/available?${params}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.data.success) {
        setProjects(response.data.data.projects);
        setPagination(response.data.data.pagination);
      }
    } catch (error) {
      console.error('Error loading projects:', error);
    } finally {
      setIsLoading(false);
    }
  }, [filters]); // Added filters as dependency

  useEffect(() => {
    if (user?.role === 'freelancer') {
      loadProjects();
    }
  }, [user, loadProjects]); // Added loadProjects as dependency

  const handleFilterChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value,
      page: 1 // Reset to first page on filter change
    }));
  };

  const handlePageChange = (page: number) => {
    setFilters(prev => ({ ...prev, page }));
  };

  const handleRequestProject = async (projectId: string) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `http://localhost:5000/api/projects/${projectId}/request`,
        {
          message: `I'm interested in working on this project and have relevant experience.`
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.data.success) {
        alert('Project request submitted successfully!');
        loadProjects(); // Reload projects to update request status
      }
    } catch (error: any) {
      console.error('Error requesting project:', error);
      alert(error.response?.data?.message || 'Error submitting request');
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getDaysRemaining = (deadline: string) => {
    const today = new Date();
    const deadlineDate = new Date(deadline);
    const diffTime = deadlineDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays > 0 ? diffDays : 0;
  };

  if (isLoading && projects.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="container mx-auto px-4 py-8">
          <div className="text-center">
            <div className="loading-spinner mx-auto mb-4"></div>
            <p className="text-gray-600">Loading projects...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navigation */}
      <nav className="navbar">
        <div className="navbar-container">
          <div className="navbar-brand">
            <div className="brand-logo">NS</div>
            <span className="brand-text">NexSync</span>
          </div>
          
          <div className="navbar-actions">
            <div className="user-info">
              <div className="user-avatar">
                {user?.username?.charAt(0).toUpperCase() || 'F'}
              </div>
              <div className="user-details">
                <span className="user-name">{user?.username?.toUpperCase()}</span>
                <div className="user-meta">
                  <span className="role-badge role-freelancer">Freelancer</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </nav>

      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Browse Projects</h1>
              <p className="text-gray-600">
                Find projects that match your skills and expertise
              </p>
            </div>
            <button 
              onClick={() => navigate('/dashboard')}
              className="btn btn-secondary"
            >
              ← Back to Dashboard
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Search
              </label>
              <input
                type="text"
                name="search"
                value={filters.search}
                onChange={handleFilterChange}
                placeholder="Search projects..."
                className="form-input"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Institution
              </label>
              <input
                type="text"
                name="institution"
                value={filters.institution}
                onChange={handleFilterChange}
                placeholder="Filter by institution..."
                className="form-input"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Budget Range
              </label>
              <div className="flex space-x-2">
                <input
                  type="number"
                  name="min_budget"
                  value={filters.min_budget}
                  onChange={handleFilterChange}
                  placeholder="Min"
                  className="form-input flex-1"
                />
                <input
                  type="number"
                  name="max_budget"
                  value={filters.max_budget}
                  onChange={handleFilterChange}
                  placeholder="Max"
                  className="form-input flex-1"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Status
              </label>
              <select
                name="status"
                value={filters.status}
                onChange={handleFilterChange}
                className="form-input"
              >
                <option value="pending">Pending</option>
                <option value="all">All</option>
              </select>
            </div>
          </div>
          
          <div className="mt-4 flex justify-between items-center">
            <div className="text-sm text-gray-600">
              {pagination.totalProjects} projects found
            </div>
            <button
              onClick={() => setFilters({
                institution: '',
                min_budget: '',
                max_budget: '',
                service_type: '',
                search: '',
                status: 'pending',
                page: 1,
                limit: 10
              })}
              className="btn btn-secondary btn-sm"
            >
              Clear Filters
            </button>
          </div>
        </div>

        {/* Projects Grid */}
        {projects.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-8 text-center">
            <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No projects found</h3>
            <p className="text-gray-600 mb-4">
              {filters.search || filters.institution || filters.min_budget || filters.max_budget
                ? 'Try adjusting your filters to find more projects.'
                : 'There are currently no available projects matching your criteria.'}
            </p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {projects.map((project) => (
                <div key={project.id} className="bg-white rounded-lg shadow hover:shadow-md transition-shadow duration-300">
                  <div className="p-6">
                    {/* Project Header */}
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-1 line-clamp-2">
                          {project.title}
                        </h3>
                        <p className="text-sm text-gray-600">
                          {project.institution}
                        </p>
                      </div>
                      <span className={`status-badge ${project.status === 'pending' ? 'pending' : 'active'}`}>
                        {project.status}
                      </span>
                    </div>

                    {/* Project Description */}
                    <p className="text-gray-700 mb-4 line-clamp-3">
                      {project.description}
                    </p>

                    {/* Services */}
                    <div className="mb-4">
                      <span className="text-sm font-medium text-gray-700">Services:</span>
                      <div className="mt-1 flex flex-wrap gap-2">
                        {project.services?.slice(0, 2).map((service: any, index: number) => (
                          <span key={index} className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                            {service.subServiceName} × {service.quantity}
                          </span>
                        ))}
                        {project.services && project.services.length > 2 && (
                          <span className="text-xs text-gray-500">
                            +{project.services.length - 2} more
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Project Details */}
                    <div className="space-y-2 mb-6">
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Budget:</span>
                        <span className="text-sm font-semibold text-green-600">
                          ₦{project.budget.toLocaleString()}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Deadline:</span>
                        <span className="text-sm font-medium">
                          {formatDate(project.deadline)}
                          <span className="text-red-600 ml-2">
                            ({getDaysRemaining(project.deadline)} days left)
                          </span>
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Posted:</span>
                        <span className="text-sm">{formatDate(project.created_at)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Client:</span>
                        <span className="text-sm">{project.client_name}</span>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex space-x-2">
                      <button
                        onClick={() => navigate(`/projects/${project.id}`)}
                        className="btn btn-secondary flex-1"
                      >
                        View Details
                      </button>
                      
                      {project.has_requested ? (
                        <button
                          disabled
                          className={`btn flex-1 ${
                            project.request_status === 'pending' ? 'btn-secondary' :
                            project.request_status === 'accepted' ? 'btn-success' :
                            'btn-danger'
                          }`}
                        >
                          {project.request_status === 'pending' ? 'Request Pending' :
                           project.request_status === 'accepted' ? 'Accepted' :
                           'Rejected'}
                        </button>
                      ) : (
                        <button
                          onClick={() => handleRequestProject(project.id)}
                          className="btn btn-primary flex-1"
                        >
                          Request Project
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div className="mt-8 flex justify-center">
                <nav className="flex items-center space-x-2">
                  <button
                    onClick={() => handlePageChange(pagination.currentPage - 1)}
                    disabled={!pagination.hasPrev}
                    className={`px-3 py-2 rounded-md ${
                      pagination.hasPrev
                        ? 'bg-white text-gray-700 hover:bg-gray-50 border'
                        : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    }`}
                  >
                    ← Previous
                  </button>
                  
                  {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                    let pageNum: number;
                    if (pagination.totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (pagination.currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (pagination.currentPage >= pagination.totalPages - 2) {
                      pageNum = pagination.totalPages - 4 + i;
                    } else {
                      pageNum = pagination.currentPage - 2 + i;
                    }
                    
                    return (
                      <button
                        key={pageNum}
                        onClick={() => handlePageChange(pageNum)}
                        className={`px-3 py-2 rounded-md ${
                          pagination.currentPage === pageNum
                            ? 'bg-blue-600 text-white'
                            : 'bg-white text-gray-700 hover:bg-gray-50 border'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                  
                  <button
                    onClick={() => handlePageChange(pagination.currentPage + 1)}
                    disabled={!pagination.hasNext}
                    className={`px-3 py-2 rounded-md ${
                      pagination.hasNext
                        ? 'bg-white text-gray-700 hover:bg-gray-50 border'
                        : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    }`}
                  >
                    Next →
                  </button>
                </nav>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default BrowseProjects;